// src/components/AIComponent/FixtureScheduler.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Upload, Calendar, Clock, Users, Settings as SettingsIcon, Zap, Download, 
  RefreshCw, MapPin, CheckCircle, HelpCircle, FileSpreadsheet, Building2,
  ChevronRight, ChevronLeft, Check, AlertCircle, X, FileText, Copy
} from 'lucide-react';
import logo from '../../assets/images/logo2.PNG';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// ===========================
// DEBUG CONFIGURATION
// ===========================
const DEBUG_CONFIG = {
  showPitchLookups: false,
  showAllocationDetails: false,
  showSectionChecks: false,
  showStrategyAttempts: true,
  showStrategyResults: true
};

// Wizard step definitions
const WIZARD_STEPS = [
  { id: 'facility', title: 'Select Facility', icon: Building2, description: 'Choose which facility to schedule fixtures at' },
  { id: 'upload', title: 'Upload Fixtures', icon: Upload, description: 'Import your fixture list' },
  { id: 'match-teams', title: 'Match Teams', icon: Users, description: 'Link teams and set preferences' },
  { id: 'review', title: 'Review & Schedule', icon: Calendar, description: 'Optimize and confirm fixtures' },
  { id: 'summary', title: 'Changes Summary', icon: FileText, description: 'Review changes for federation notification' }
];

// =============================================================================
// VALID SECTION GROUPINGS - Matches UnifiedPitchAllocator configurations
// =============================================================================
const VALID_SECTION_GROUPINGS = {
  // Single section (eighth) - any individual section
  single: [
    ['A'], ['B'], ['C'], ['D'], ['E'], ['F'], ['G'], ['H']
  ],
  // Quarter pitch - vertically adjacent pairs (2 sections)
  quarter: [
    ['A', 'C'], ['B', 'D'], ['C', 'E'], ['D', 'F'], ['E', 'G'], ['F', 'H']
  ],
  // Half pitch - 4 sections forming a half
  half: [
    ['A', 'B', 'C', 'D'], ['C', 'D', 'E', 'F'], ['E', 'F', 'G', 'H']
  ],
  // Full pitch - all 8 sections
  full: [
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
  ]
};

// Helper function to get valid section groupings based on matchArea
const getValidGroupingsForMatchArea = (matchArea, availableSections) => {
  const matchAreaLower = (matchArea || 'half pitch').toLowerCase().trim();
  
  let groupingKey = 'half'; // Default
  if (matchAreaLower === 'single section' || matchAreaLower === 'under 6 & 7 size' || 
      matchAreaLower === '1 section' || matchAreaLower === '1' || matchAreaLower === 'eighth' ||
      matchAreaLower === 'small') {
    groupingKey = 'single';
  } else if (matchAreaLower === 'quarter pitch' || matchAreaLower === '2 sections' || 
             matchAreaLower === 'quarter' || matchAreaLower === '2') {
    groupingKey = 'quarter';
  } else if (matchAreaLower === 'half pitch' || matchAreaLower === '4 sections' || 
             matchAreaLower === 'half' || matchAreaLower === '4') {
    groupingKey = 'half';
  } else if (matchAreaLower === 'full pitch' || matchAreaLower === '8 sections' || 
             matchAreaLower === 'full' || matchAreaLower === '8') {
    groupingKey = 'full';
  }
  
  const allGroupings = VALID_SECTION_GROUPINGS[groupingKey] || VALID_SECTION_GROUPINGS.half;
  
  // Filter groupings to only include those where all sections exist on this pitch
  const availableSet = new Set(availableSections);
  const validGroupings = allGroupings.filter(grouping => 
    grouping.every(section => availableSet.has(section))
  );
  
  return validGroupings;
};

const FixtureScheduler = () => {
  const navigate = useNavigate();
  
  // ===================
  // STATE MANAGEMENT
  // ===================
  
  // Auth & Club State
  const [user, setUser] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [clubData, setClubData] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('basic');
  
  // Facilities State
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  // Wizard State
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // Paywall State
  const [showPaywall, setShowPaywall] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  
  // Fixture State
  const [fixtures, setFixtures] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [originalFixtures, setOriginalFixtures] = useState([]);
  
  // Multi-team mapping state
  const [detectedDriblTeams, setDetectedDriblTeams] = useState([]);
  const [teamMappings, setTeamMappings] = useState({});
  const [skippedTeams, setSkippedTeams] = useState([]);
  
  // File Upload & Column Mapping State
  const [uploadedFileData, setUploadedFileData] = useState(null); // Raw parsed rows
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    homeClubName: '',
    awayClubName: '',
    date: '',
    time: '',
    round: '',
    homeTeam: ''
  });
  const [filterClubName, setFilterClubName] = useState(''); // Club name to filter home games
  
  // Optimization State
  const [hasOptimized, setHasOptimized] = useState(false);
  const [scheduledFixtureIds, setScheduledFixtureIds] = useState(new Set());
  
  // Success Modal State
  const [schedulingSuccessModal, setSchedulingSuccessModal] = useState({
    isOpen: false,
    fixture: null,
    pitchUrl: null
  });
  
  // Settings Data (from Firebase)
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availablePitches, setAvailablePitches] = useState([]);
  const [pitchSectionConfigs, setPitchSectionConfigs] = useState({});
  
  // Loading States
  const [loading, setLoading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [schedulingFixture, setSchedulingFixture] = useState(null);
  
  // Mobile Detection
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  
  // Template Guide Modal
  const [showTemplateGuide, setShowTemplateGuide] = useState(false);
  
  // Per-team scheduling preferences
  // { [pitchalloTeamName]: { preferredDay, preferredTimeSlot, preferredPitch } }
  const [teamPreferences, setTeamPreferences] = useState({});

  // Helper to update one field for one team
  const updateTeamPref = (teamName, field, value) => {
    setTeamPreferences(prev => ({
      ...prev,
      [teamName]: { ...(prev[teamName] || {}), [field]: value }
    }));
  };

  // Default prefs for a team (fallback when not yet set)
  const defaultPrefsForTeam = (teamName) => ({
    preferredDay: 'Sunday',
    preferredTimeSlot: '08:00-11:00',
    preferredPitch: availablePitches[0]?.id || ''
  });

  const getTeamPrefs = (teamName) => ({
    ...defaultPrefsForTeam(teamName),
    ...(teamPreferences[teamName] || {})
  });

  // Summary Copy State
  const [summaryCopied, setSummaryCopied] = useState(false);

  // Validation Modal State
  const [validationModal, setValidationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error' // 'error', 'warning', 'info', 'success'
  });

  // Helper to show validation modal
  const showValidationModal = (title, message, type = 'error') => {
    setValidationModal({ isOpen: true, title, message, type });
  };

  const closeValidationModal = () => {
    setValidationModal({ isOpen: false, title: '', message: '', type: 'error' });
  };

  // ===================
  // HELPER FUNCTIONS
  // ===================
  
  const naturalSort = (arr, key = 'name') => {
    return arr.slice().sort((a, b) => {
      const aVal = (a[key] || a).toString();
      const bVal = (b[key] || b).toString();
      const aParts = aVal.match(/(\d+|[a-z]+)/gi) || [];
      const bParts = bVal.match(/(\d+|[a-z]+)/gi) || [];
      
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] || '';
        const bPart = bParts[i] || '';
        
        if (!isNaN(aPart) && !isNaN(bPart)) {
          const diff = parseInt(aPart) - parseInt(bPart);
          if (diff !== 0) return diff;
        } else {
          const cmp = aPart.toLowerCase().localeCompare(bPart.toLowerCase());
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  };

  // Get sections required for a matchArea
  const getMatchAreaSectionsNeeded = (matchArea) => {
    const matchAreaLower = (matchArea || 'half pitch').toLowerCase().trim();
    
    if (matchAreaLower === 'single section' || matchAreaLower === 'under 6 & 7 size' || 
        matchAreaLower === '1 section' || matchAreaLower === '1' || matchAreaLower === 'eighth' ||
        matchAreaLower === 'small') {
      return 1;
    } else if (matchAreaLower === 'quarter pitch' || matchAreaLower === '2 sections' || 
               matchAreaLower === 'quarter' || matchAreaLower === '2') {
      return 2;
    } else if (matchAreaLower === 'half pitch' || matchAreaLower === '4 sections' || 
               matchAreaLower === 'half' || matchAreaLower === '4') {
      return 4;
    } else if (matchAreaLower === 'full pitch' || matchAreaLower === '8 sections' || 
               matchAreaLower === 'full' || matchAreaLower === '8') {
      return 8;
    }
    return 4; // Default to half pitch
  };

  // Check if a team can play at any pitch in the facility
  const canTeamPlayAtFacility = (team) => {
    if (!team || !availablePitches || availablePitches.length === 0) return { canPlay: false, reason: 'No pitches available' };
    
    const sectionsNeeded = getMatchAreaSectionsNeeded(team.matchArea);
    
    // Check each pitch to see if any can accommodate the team
    for (const pitch of availablePitches) {
      const pitchSections = getPitchSections(pitch.id);
      if (pitchSections.length >= sectionsNeeded) {
        // Also check if valid groupings exist
        const validGroupings = getValidGroupingsForMatchArea(team.matchArea, pitchSections);
        if (validGroupings.length > 0) {
          return { canPlay: true, pitch: pitch.name || pitch.id };
        }
      }
    }
    
    // Find the max sections available at any pitch
    let maxSections = 0;
    for (const pitch of availablePitches) {
      const pitchSections = getPitchSections(pitch.id);
      maxSections = Math.max(maxSections, pitchSections.length);
    }
    
    return { 
      canPlay: false, 
      reason: `Team requires ${team.matchArea || 'Full Pitch'} (${sectionsNeeded} sections) but the largest pitch at this facility only has ${maxSections} section${maxSections !== 1 ? 's' : ''}.`,
      sectionsNeeded,
      maxAvailable: maxSections
    };
  };

  // Handle team selection with validation
  const handleTeamSelection = (teamName) => {
    const team = availableTeams.find(t => t.name === teamName);
    if (!team) return;
    
    // Check if team can play at this facility
    const validation = canTeamPlayAtFacility(team);
    
    if (!validation.canPlay) {
      showValidationModal(
        'Team Cannot Play at This Facility',
        `"${team.name}" requires ${team.matchArea || 'Full Pitch'} (${validation.sectionsNeeded} sections) but the largest pitch at "${selectedFacility?.name || 'this facility'}" only has ${validation.maxAvailable} section${validation.maxAvailable !== 1 ? 's' : ''}.\n\nPlease either:\n• Select a different facility with larger pitches\n• Update the team's match requirements in Team Management`,
        'error'
      );
      return;
    }
    
    setSelectedTeam(teamName);
  };

  const normalizePitchId = (pitchId) => {
    if (!pitchId) return '';
    return pitchId.toString().toLowerCase().replace(/\s+/g, '-');
  };

  const getPitchSections = (pitchId) => {
    const normalizedId = normalizePitchId(pitchId);
    const possibleKeys = [normalizedId, `pitch-${pitchId}`, pitchId, `Pitch ${pitchId}`, `Pitch-${pitchId}`];
    
    for (const key of possibleKeys) {
      const config = pitchSectionConfigs[key];
      if (config) {
        const sectionsCount = config.sectionsCount || 8;
        return Array.from({ length: sectionsCount }, (_, i) => String.fromCharCode(65 + i));
      }
    }
    return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  };

  const getAvailableTimeSlots = () => {
    const slots = [];
    for (let h = 8; h <= 20; h++) {
      const nextH = h + 3;
      if (nextH <= 21) {
        const start = `${h.toString().padStart(2, '0')}:00`;
        const end = `${nextH.toString().padStart(2, '0')}:00`;
        slots.push(`${start}-${end}`);
      }
    }
    return slots;
  };

  const parseTimeSlotRange = (timeSlotRange) => {
    const [start] = timeSlotRange.split('-');
    const [startHour, startMin] = start.split(':').map(Number);
    return { startHour, startMin: startMin || 0 };
  };

  // ===================
  // DEVICE DETECTION
  // ===================
  
  useEffect(() => {
    const checkDevice = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // ===================
  // DATA LOADING
  // ===================
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role);
            const userClubId = userData.clubId;
            setClubId(userClubId);
            
            const clubDoc = await getDoc(doc(db, 'clubs', userClubId));
            if (clubDoc.exists()) {
              const clubDataFromDb = clubDoc.data();
              setClubData(clubDataFromDb);
              setClubName(clubDataFromDb.name || 'Your Club');
              
              // Check trial expiration
              const now = new Date();
              const trialEndsAt = clubDataFromDb.trialEndsAt?.toDate ? 
                clubDataFromDb.trialEndsAt.toDate() : 
                new Date(clubDataFromDb.trialEndsAt);
              const subscriptionStatus = clubDataFromDb.subscriptionStatus || 'trial';
              const msRemaining = trialEndsAt - now;
              const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
              const isTrialExpired = subscriptionStatus === 'trial' && daysRemaining <= 0;
              
              if (isTrialExpired) {
                setShowTrialExpiredModal(true);
                setSettingsLoading(false);
                return;
              }
              
              // Check Premium access
              const currentTier = clubDataFromDb.subscriptionTier || 'basic';
              setSubscriptionTier(currentTier);
              
              if (currentTier !== 'premium') {
                setShowPaywall(true);
                setSettingsLoading(false);
                return;
              }
              
              // Load facilities
              const facilitiesSnapshot = await getDocs(collection(db, 'clubs', userClubId, 'facilities'));
              const facilitiesList = [];
              facilitiesSnapshot.forEach(doc => {
                facilitiesList.push({ id: doc.id, ...doc.data() });
              });
              
              // Sort facilities: primary first, then by name
              const primaryFacilityId = clubDataFromDb.primaryFacilityId;
              facilitiesList.sort((a, b) => {
                if (a.id === primaryFacilityId) return -1;
                if (b.id === primaryFacilityId) return 1;
                return (a.name || '').localeCompare(b.name || '');
              });
              
              setFacilities(facilitiesList);
              console.log('🏟️ Facilities loaded:', facilitiesList.length);
              
              // Auto-select if single facility or set primary
              if (facilitiesList.length === 1) {
                setSelectedFacilityId(facilitiesList[0].id);
                setSelectedFacility(facilitiesList[0]);
                // Skip facility step for single facility clubs
                setCurrentStep(1);
                setCompletedSteps(new Set([0]));
              } else if (facilitiesList.length > 1 && primaryFacilityId) {
                const primary = facilitiesList.find(f => f.id === primaryFacilityId);
                if (primary) {
                  setSelectedFacilityId(primary.id);
                  setSelectedFacility(primary);
                }
              }
              
              // Load settings
              const settingsDoc = await getDoc(doc(db, 'clubs', userClubId, 'settings', 'general'));
              if (settingsDoc.exists()) {
                const settings = settingsDoc.data();
                
                // Load teams
                if (settings.teams) {
                  let teamsArray;
                  if (Array.isArray(settings.teams)) {
                    teamsArray = settings.teams.map((team, idx) => ({
                      ...team,
                      id: team.id || `team_legacy_${idx}`
                    }));
                  } else {
                    teamsArray = Object.entries(settings.teams).map(([key, team]) => ({
                      ...team,
                      id: key,
                      name: team.name || key
                    }));
                  }
                  const sortedTeams = naturalSort(teamsArray);
                  setAvailableTeams(sortedTeams);
                }
                
                // Load pitch section configs
                if (settings.pitchSectionConfigurations) {
                  setPitchSectionConfigs(settings.pitchSectionConfigurations);
                }
              }
              
              setSettingsLoading(false);
            }
          }
        } catch (error) {
          console.error('Error loading user/club data:', error);
          setSettingsLoading(false);
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // ===================
  // FACILITY SELECTION HANDLER
  // ===================
  
  useEffect(() => {
    const loadFacilityPitches = async () => {
      if (!selectedFacilityId || !clubId) return;
      
      const facility = facilities.find(f => f.id === selectedFacilityId);
      if (!facility) return;
      
      setSelectedFacility(facility);
      
      console.log('📦 [FixtureWizard] Loading pitches for facility:', selectedFacilityId);
      console.log('📦 [FixtureWizard] Facility object:', facility);
      
      // Fetch the facility document directly to ensure we get full data
      const facilityDoc = await getDoc(doc(db, 'clubs', clubId, 'facilities', selectedFacilityId));
      let pitchBoundaries = [];
      let facilityPitchNames = {};
      
      if (facilityDoc.exists()) {
        const facilityData = facilityDoc.data();
        console.log('🛰️ [FixtureWizard] Facility doc data:', facilityData);
        
        // Check multiple possible locations for pitchBoundaries:
        // 1. Directly on facility doc as 'pitchBoundaries' (common structure)
        // 2. Directly on facility doc as 'pitches' (alternative name)
        // 3. Inside satelliteConfig (legacy structure)
        pitchBoundaries = facilityData.pitchBoundaries || 
                          facilityData.pitches ||
                          facilityData.satelliteConfig?.pitchBoundaries || 
                          facilityData.satelliteConfig?.pitches ||
                          [];
        
        // Log all the keys to debug
        console.log('🔑 [FixtureWizard] Facility doc keys:', Object.keys(facilityData));
        
        // Also check for pitchNames on facility doc
        facilityPitchNames = facilityData.pitchNames || 
                            facilityData.satelliteConfig?.pitchNames || 
                            {};
        
        console.log('🛰️ [FixtureWizard] pitchBoundaries found:', pitchBoundaries.length);
        console.log('🛰️ [FixtureWizard] pitchNames on facility:', Object.keys(facilityPitchNames).length);
      }
      
      // If no pitches in facility, try the legacy club-level satelliteConfig
      if (pitchBoundaries.length === 0) {
        console.log('⚠️ [FixtureWizard] No pitches in facility doc, checking club-level satelliteConfig');
        const clubDoc = await getDoc(doc(db, 'clubs', clubId));
        if (clubDoc.exists()) {
          const clubData = clubDoc.data();
          pitchBoundaries = clubData.pitchBoundaries || 
                           clubData.satelliteConfig?.pitchBoundaries || 
                           [];
          console.log('🛰️ [FixtureWizard] pitchBoundaries from club:', pitchBoundaries.length);
        }
      }
      
      // Load pitch names from settings (fallback) and merge with facility pitch names
      const settingsDoc = await getDoc(doc(db, 'clubs', clubId, 'settings', 'general'));
      const settingsPitchNames = settingsDoc.exists() ? settingsDoc.data().pitchNames || {} : {};
      
      // Merge: facility pitch names take priority over settings
      const pitchNames = { ...settingsPitchNames, ...facilityPitchNames };
      
      console.log('📋 [FixtureWizard] Merged pitch names:', pitchNames);
      
      const pitchesArray = pitchBoundaries.map((pitch, index) => {
        const pitchId = pitch.pitchId || `pitch-${pitch.pitchNumber || index + 1}`;
        return {
          id: pitchId,
          name: pitchNames[pitchId] || pitch.name || `Pitch ${pitch.pitchNumber || index + 1}`,
          number: pitch.pitchNumber || index + 1,
          facilityId: selectedFacilityId
        };
      });
      
      // Sort pitches naturally
      const sortedPitches = naturalSort(pitchesArray, 'name');
      setAvailablePitches(sortedPitches);
      
      console.log('✅ [FixtureWizard] Pitches loaded for facility:', sortedPitches.length, sortedPitches.map(p => p.name));
      
      // Default pitch is handled per-team via getTeamPrefs()
    };
    
    loadFacilityPitches();
  }, [selectedFacilityId, clubId, facilities]);

  // ===================
  // WIZARD NAVIGATION
  // ===================
  
  const canProceedToStep = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Facility
        return true;
      case 1: // Upload
        return facilities.length <= 1 || selectedFacilityId;
      case 2: // Match Teams
        return uploadedFileData !== null && (columnMapping.homeClubName || columnMapping.homeTeam) && filterClubName.trim();
      case 3: // Review
        return fixtures.length > 0 && availablePitches.length > 0;
      case 4: // Summary
        return scheduledFixtureIds.size > 0;
      default:
        return false;
    }
  };

  const goToNextStep = () => {
    if (currentStep < WIZARD_STEPS.length - 1 && canProceedToStep(currentStep + 1)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex <= currentStep || completedSteps.has(stepIndex) || canProceedToStep(stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };

  // ===================
  // FILE UPLOAD - Parse and extract headers
  // ===================
  
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    
    try {
      const fileName = file.name.toLowerCase();
      let headers = [];
      let rows = [];
      
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Parse Excel file using SheetJS
        const arrayBuffer = await file.arrayBuffer();
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        if (jsonData.length === 0) {
          throw new Error('Excel file is empty');
        }
        
        headers = jsonData[0].map((h, i) => h?.toString().trim() || `Column ${i + 1}`);
        rows = jsonData.slice(1).map(row => {
          const rowObj = {};
          headers.forEach((header, i) => {
            rowObj[header] = row[i]?.toString().trim() || '';
          });
          return rowObj;
        });
        
      } else {
        // Parse CSV file
        const text = await file.text();
        const lines = text.trim().split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          throw new Error('File is empty');
        }
        
        const separator = lines[0].includes('\t') ? '\t' : ',';
        headers = lines[0].split(separator).map((h, i) => h?.trim() || `Column ${i + 1}`);
        
        rows = lines.slice(1).map(line => {
          const values = line.split(separator);
          const rowObj = {};
          headers.forEach((header, i) => {
            rowObj[header] = values[i]?.trim() || '';
          });
          return rowObj;
        });
      }
      
      console.log('📄 [FixtureWizard] Parsed file:', { headers, rowCount: rows.length });
      
      // Store parsed data
      setUploadedFileData(rows);
      setDetectedHeaders(headers);
      
      // Try to auto-detect common column names
      const autoMapping = { ...columnMapping };
      headers.forEach(header => {
        const lowerHeader = header.toLowerCase();
        if (lowerHeader.includes('home') && lowerHeader.includes('club')) {
          autoMapping.homeClubName = header;
        } else if (lowerHeader.includes('away') && lowerHeader.includes('club')) {
          autoMapping.awayClubName = header;
        } else if (lowerHeader === 'date' || lowerHeader.includes('match date')) {
          autoMapping.date = header;
        } else if (lowerHeader === 'start' || lowerHeader === 'time' || lowerHeader.includes('kick')) {
          autoMapping.time = header;
        } else if (lowerHeader === 'round' || lowerHeader.includes('round')) {
          autoMapping.round = header;
        } else if (lowerHeader.includes('home') && lowerHeader.includes('team') && !lowerHeader.includes('club')) {
          autoMapping.homeTeam = header;
        }
      });
      
      setColumnMapping(autoMapping);
      
      // Pre-fill club name filter
      if (!filterClubName && clubName) {
        setFilterClubName(clubName);
      }

      // Reset team mappings when a new file is loaded
      setDetectedDriblTeams([]);
      setTeamMappings({});
      setSkippedTeams([]);
      setFixtures([]);
      setOriginalFixtures([]);
      setScheduledFixtureIds(new Set());
      
    } catch (error) {
      console.error('Error parsing file:', error);
      showValidationModal('File Error', `Error parsing file: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [clubName, filterClubName, columnMapping]);

  // ===================
  // PROCESS FIXTURES - Apply column mapping
  // ===================
  
  // Determines whether a row belongs to our club's home fixtures.
  // Supports two formats:
  //   Format A (Dribl): has a dedicated Home Club Name column — exact match
  //   Format B (FSA):   no Home Club Name column — contains-match on Home Team
  const isHomeRow = useCallback((row) => {
    const club = filterClubName.trim().toLowerCase();
    if (!club) return false;

    // Format A — dedicated Home Club Name column: exact match
    if (columnMapping.homeClubName) {
      const homeClubValue = (row[columnMapping.homeClubName] || '').trim().toLowerCase();
      if (homeClubValue === club) return true;
      // Fall through — if exact match fails, the column may be a full team name (FSA style)
    }

    // Format B — club name embedded in Home Team value
    // e.g. "Eastern United Under 10's Wallabies Central" starts with "Eastern United "
    if (columnMapping.homeTeam) {
      const homeTeamValue = (row[columnMapping.homeTeam] || '').trim().toLowerCase();
      return homeTeamValue.startsWith(club + ' ') || homeTeamValue === club;
    }

    return false;
  }, [filterClubName, columnMapping]);

  // Shared date parser used by processFixtures and applyTeamMappings
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const str = dateStr.toString().trim();
    let match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const [_, day, month, year] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    match = str.match(/^\d{4}-\d{2}-\d{2}$/);
    if (match) return str;
    match = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (match) {
      const [_, day, month, year] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    if (!isNaN(str) && Number(str) > 40000 && Number(str) < 60000) {
      const serialNumber = Number(str);
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const date = new Date(excelEpoch.getTime() + serialNumber * 24 * 60 * 60 * 1000);
      return date.toISOString().split('T')[0];
    }
    const parsed = new Date(str);
    if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 2000) {
      return parsed.toISOString().split('T')[0];
    }
    console.warn('[FixtureWizard] Could not parse date:', str);
    return str;
  };

  // processFixtures: scans file for home rows, detects distinct team names.
  // Date is NOT required — missing dates are flagged later as TBD.
  const processFixtures = useCallback(() => {
    if (!uploadedFileData || uploadedFileData.length === 0) {
      showValidationModal('No Data', 'No file data to process. Please upload a fixture file.', 'error');
      return;
    }
    const hasClubColumn = !!columnMapping.homeClubName;
    const hasTeamColumn = !!columnMapping.homeTeam;
    if (!hasClubColumn && !hasTeamColumn) {
      showValidationModal('Missing Columns', 'Please select either the Home Club Name or Home Team column so we can identify your home games.', 'warning');
      return;
    }
    if (!filterClubName.trim()) {
      showValidationModal('Club Name Required', 'Please enter your club name to filter home games.', 'warning');
      return;
    }

    // Count home rows per team — include rows with missing dates (flagged later)
    const teamCounts = {};
    const teamDateless = {};
    uploadedFileData.forEach(row => {
      if (!isHomeRow(row)) return;
      const awayClub = (row[columnMapping.awayClubName] || '').trim();
      if (awayClub.toLowerCase() === 'bye') return;
      const homeClubValue = columnMapping.homeClubName ? (row[columnMapping.homeClubName] || '').trim() : '';
      const driblTeamName = (columnMapping.homeTeam
        ? (row[columnMapping.homeTeam] || homeClubValue)
        : homeClubValue).trim();
      if (!driblTeamName) return;
      teamCounts[driblTeamName] = (teamCounts[driblTeamName] || 0) + 1;
      const rawDate = columnMapping.date ? (row[columnMapping.date] || '') : '';
      if (!rawDate || !parseDate(rawDate)) {
        teamDateless[driblTeamName] = (teamDateless[driblTeamName] || 0) + 1;
      }
    });

    const detected = Object.entries(teamCounts).map(([driblName, fixtureCount]) => ({
      driblName,
      fixtureCount,
      datelessCount: teamDateless[driblName] || 0
    }));

    if (detected.length === 0) {
      showValidationModal(
        'No Home Fixtures Found',
        `No home fixtures found for "${filterClubName}".\n\nCheck that:\n• Your club name matches exactly (e.g. "Eastern United")\n• The correct Home Team or Home Club Name column is selected`,
        'warning'
      );
      return;
    }

    console.log('✅ [FixtureWizard] Detected Dribl teams:', detected);
    setDetectedDriblTeams(detected);

    // Auto-suggest mappings: if a Pitchallo team name contains words from the Dribl name, pre-select it
    const autoMappings = {};
    detected.forEach(({ driblName }) => {
      const driblWords = driblName.toLowerCase().split(/\s+/);
      let bestMatch = null;
      let bestScore = 0;
      availableTeams.forEach(team => {
        const teamWords = team.name.toLowerCase().split(/\s+/);
        const score = teamWords.filter(w => driblWords.some(d => d.includes(w) || w.includes(d))).length;
        if (score > bestScore) { bestScore = score; bestMatch = team.name; }
      });
      autoMappings[driblName] = bestScore > 0 ? bestMatch : '';
    });
    setTeamMappings(autoMappings);

    setHasOptimized(false);
    setScheduledFixtureIds(new Set());
    setFixtures([]);
    setOriginalFixtures([]);

    // Advance to match-teams step
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(currentStep + 1);

  }, [uploadedFileData, columnMapping, filterClubName, currentStep, availableTeams, isHomeRow]);

  // applyTeamMappings: called from match-teams step. Builds final fixture list
  // using confirmed mappings. Skipped teams are recorded for the summary.
  const applyTeamMappings = useCallback(() => {
    const mapped = detectedDriblTeams.filter(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__');
    const skipped = detectedDriblTeams.filter(t => !teamMappings[t.driblName] || teamMappings[t.driblName] === '__skip__');

    if (mapped.length === 0) {
      showValidationModal('No Teams Mapped', 'Please link at least one team to a Pitchallo team before continuing.', 'warning');
      return;
    }

    // Validate all mapped teams can play at this facility
    for (const { driblName } of mapped) {
      const pitchalloTeamName = teamMappings[driblName];
      const teamObject = availableTeams.find(t => t.name === pitchalloTeamName);
      if (!teamObject) continue;
      const validation = canTeamPlayAtFacility(teamObject);
      if (!validation.canPlay) {
        showValidationModal(
          'Team Cannot Play at This Facility',
          `"${pitchalloTeamName}" requires ${teamObject.matchArea || 'Full Pitch'} (${validation.sectionsNeeded} sections) but the largest pitch at "${selectedFacility?.name || 'this facility'}" only has ${validation.maxAvailable} section${validation.maxAvailable !== 1 ? 's' : ''}.\n\nPlease either select a different facility or update the team's match requirements in Team Management.`,
          'error'
        );
        return;
      }
    }

    // Build fixture list — include rows with missing dates, flagged as TBD
    const allFixtures = [];
    uploadedFileData.forEach((row, index) => {
      if (!isHomeRow(row)) return;

      const homeClubValue = columnMapping.homeClubName ? (row[columnMapping.homeClubName] || '').trim() : '';
      const driblTeamName = (columnMapping.homeTeam
        ? (row[columnMapping.homeTeam] || homeClubValue)
        : homeClubValue).trim();
      if (!driblTeamName) return;

      const pitchalloTeamName = teamMappings[driblTeamName];
      if (!pitchalloTeamName || pitchalloTeamName === '__skip__') return;

      const awayClub = (row[columnMapping.awayClubName] || '').trim();
      if (awayClub.toLowerCase() === 'bye') return;

      // Date — may be missing for draft/TBD fixtures
      const rawDate = columnMapping.date ? (row[columnMapping.date] || '') : '';
      const parsedDate = rawDate ? parseDate(rawDate) : null;
      const dateStatus = parsedDate ? 'confirmed' : 'tbd';

      // Time
      let time = null;
      if (columnMapping.time) {
        const rawTime = row[columnMapping.time];
        if (rawTime) {
          if (!isNaN(rawTime) && Number(rawTime) >= 0 && Number(rawTime) < 1) {
            const totalMinutes = Math.round(Number(rawTime) * 24 * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          } else if (typeof rawTime === 'string' && rawTime.includes(':')) {
            time = rawTime.substring(0, 5);
          } else if (rawTime) {
            time = rawTime.toString();
          }
        }
      }

      const round = columnMapping.round ? String(row[columnMapping.round] || '').trim() : '';

      allFixtures.push({
        id: `fixture-${index}`,
        originalDate: parsedDate || null,
        originalTime: time || null,
        currentDate: parsedDate || null,
        currentTime: time || null,
        round,
        homeTeam: driblTeamName,
        pitchalloTeam: pitchalloTeamName,
        awayTeam: awayClub,
        homeClubName: homeClubValue || filterClubName,
        status: 'Pending',
        dateStatus,           // 'confirmed' | 'tbd'
        isBye: false,
        originalIndex: index,
        isHome: true,
        scheduled: false
      });
    });

    if (allFixtures.length === 0) {
      showValidationModal('No Fixtures Found', 'No home fixtures found for the mapped teams. Check that your club name matches the file exactly.', 'warning');
      return;
    }

    const tbdCount = allFixtures.filter(f => f.dateStatus === 'tbd').length;
    if (tbdCount > 0) {
      console.log(`ℹ️ [FixtureWizard] ${tbdCount} fixtures have no date (TBD) — will be flagged in review`);
    }

    console.log('✅ [FixtureWizard] Built fixture list:', allFixtures.length, 'fixtures across', mapped.length, 'teams');
    setSkippedTeams(skipped);
    setFixtures(allFixtures);
    setOriginalFixtures([...allFixtures]);

    // Seed default preferences for any newly mapped team that doesn't have prefs yet
    setTeamPreferences(prev => {
      const updated = { ...prev };
      mapped.forEach(({ driblName }) => {
        const pitchalloName = teamMappings[driblName];
        if (pitchalloName && !updated[pitchalloName]) {
          updated[pitchalloName] = {
            preferredDay: 'Sunday',
            preferredTimeSlot: '08:00-11:00',
            preferredPitch: availablePitches[0]?.id || ''
          };
        }
      });
      return updated;
    });

    // Advance to review step
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(currentStep + 1);

  }, [detectedDriblTeams, teamMappings, uploadedFileData, columnMapping, filterClubName, currentStep, availableTeams, selectedFacility, isHomeRow]);

  // Update a single fixture's date inline (for TBD fixtures the coordinator fills in manually)
  const updateFixtureDate = useCallback((fixtureId, newDate) => {
    setFixtures(prev => prev.map(f => {
      if (f.id !== fixtureId) return f;
      const hasDate = !!newDate;
      return {
        ...f,
        currentDate: newDate || null,
        originalDate: newDate || null,
        dateStatus: hasDate ? 'confirmed' : 'tbd',
        status: hasDate ? 'Pending' : 'TBD',
        optimizedReason: null,
        optimized: false,
        assignedPitch: null,
        assignedSections: null,
        pitchName: null,
        scheduled: false
      };
    }));
    setHasOptimized(false);
  }, []);

  // ===================
  // AI OPTIMIZATION
  // ===================
  
  const optimizeWithAI = async () => {
    if (fixtures.length === 0) {
      showValidationModal('No Fixtures', 'Please upload fixtures first before optimizing.', 'warning');
      return;
    }
    
    if (!clubId || availablePitches.length === 0) {
      showValidationModal('Missing Configuration', 'Please ensure facility and pitches are configured before optimizing.', 'error');
      return;
    }

    setAiProcessing(true);

    try {
      const optimizedFixtures = [];
      // Shared in-memory section tracker: key = "pitchId|date|time|section"
      // Prevents two fixtures being assigned the same sections before either is saved
      const pendingSections = new Set();
      let slotsFound = 0;
      let slotsRescheduled = 0;

      // Per-team prefs are resolved inside the fixture loop below

      // Preload existing Firestore allocations for all pitches
      const allocationCache = {};
      for (const pitch of availablePitches) {
        const allocationsRef = collection(db, 'clubs', clubId, 'allocations', pitch.id, 'items');
        const snapshot = await getDocs(allocationsRef);
        const allocations = {};
        snapshot.forEach(doc => { allocations[doc.id] = doc.data(); });
        allocationCache[pitch.id] = allocations;
      }

      // Per-fixture slot checker — uses team's own matchArea and duration
      const checkSlotAvailability = (targetDate, targetTime, pitchId, fixtureMatchArea, fixtureDuration) => {
        try {
          const allocations = allocationCache[pitchId] || {};
          const allSections = getPitchSections(pitchId);
          const [hours, minutes] = targetTime.split(':').map(Number);
          const startMinutes = hours * 60 + minutes;
          const endMinutes = startMinutes + fixtureDuration;

          const validGroupings = getValidGroupingsForMatchArea(fixtureMatchArea, allSections);
          if (validGroupings.length === 0) return null;

          // Sections occupied by Firestore allocations
          const occupiedSections = new Set();
          Object.values(allocations).forEach(allocation => {
            if (allocation.date !== targetDate) return;
            const [aH, aM] = allocation.startTime.split(':').map(Number);
            const aStart = aH * 60 + aM;
            const aDur = allocation.actualAllocatedTime || allocation.duration || 60;
            const aEnd = aStart + aDur;
            if (startMinutes < aEnd && endMinutes > aStart) {
              occupiedSections.add(allocation.section);
            }
          });

          // Also block sections claimed by earlier fixtures in this optimization run
          for (const grouping of validGroupings) {
            const allAvailable = grouping.every(section => {
              if (occupiedSections.has(section)) return false;
              // Check in-memory pending tracker
              const key = `${pitchId}|${targetDate}|${targetTime}|${section}`;
              return !pendingSections.has(key);
            });
            if (allAvailable) {
              return {
                date: targetDate,
                time: targetTime,
                pitch: pitchId,
                sections: grouping,
                availableCount: allSections.length - occupiedSections.size
              };
            }
          }
          return null;
        } catch (error) {
          console.error('Error checking availability:', error);
          return null;
        }
      };

      // Per-team usedDates set — prevents same team double-booked on same day
      // Pre-populate from existing Firestore allocations so previous test runs don't cause conflicts
      const teamUsedDates = {};

      // Build a map of existing scheduled dates per team from Firestore allocations
      for (const pitch of availablePitches) {
        const allocations = allocationCache[pitch.id] || {};
        Object.values(allocations).forEach(allocation => {
          if (!allocation.team || !allocation.date) return;
          // Match allocation team name against pitchallo team names
          const matchedTeam = availableTeams.find(t => 
            t.name === allocation.team || 
            allocation.team.includes(t.name) || 
            t.name.includes(allocation.team)
          );
          if (matchedTeam) {
            if (!teamUsedDates[matchedTeam.name]) teamUsedDates[matchedTeam.name] = new Set();
            teamUsedDates[matchedTeam.name].add(allocation.date);
          }
        });
      }
      console.log('📅 [FixtureWizard] Pre-populated usedDates from Firestore:', 
        Object.entries(teamUsedDates).map(([t, d]) => `${t}: ${d.size} dates`));

      for (const fixture of fixtures) {
        if (fixture.isBye) { optimizedFixtures.push(fixture); continue; }

        const newFixture = { ...fixture };

        // TBD fixtures — date not yet assigned by the league
        if (fixture.dateStatus === 'tbd' || !fixture.originalDate) {
          newFixture.optimizedReason = 'Date TBD — not yet assigned by the league';
          newFixture.status = 'TBD';
          optimizedFixtures.push(newFixture);
          continue;
        }

        // Resolve team object and per-team preferences for this fixture
        const pitchalloTeamName = fixture.pitchalloTeam;
        const teamObject = availableTeams.find(t => t.name === pitchalloTeamName);
        if (!teamObject) {
          newFixture.optimizedReason = `Team "${pitchalloTeamName}" not found in settings`;
          optimizedFixtures.push(newFixture);
          continue;
        }

        console.log(`🏃 [FixtureWizard] Resolving team: "${pitchalloTeamName}" → matchArea: "${teamObject.matchArea}", matchDuration: "${teamObject.matchDuration}"`);

        const durationStr = teamObject.matchDuration || teamObject.duration || '60';
        const baseDuration = parseInt(durationStr.toString().replace(/\D/g, '')) || 60;
        const fixtureDuration = Math.ceil((baseDuration + 10) / 15) * 15;
        const fixtureMatchArea = teamObject.matchArea || 'Half Pitch';

        // Load this team's preferences
        const teamPrefs = getTeamPrefs(pitchalloTeamName);
        const { startHour } = parseTimeSlotRange(teamPrefs.preferredTimeSlot);
        const preferredDay = teamPrefs.preferredDay;
        const preferredPitch = teamPrefs.preferredPitch || (availablePitches[0]?.id || '');

        if (!teamUsedDates[pitchalloTeamName]) teamUsedDates[pitchalloTeamName] = new Set();
        const usedDates = teamUsedDates[pitchalloTeamName];

        try {
          let foundSlot = false;
          const [year, month, day] = fixture.originalDate.split('-').map(Number);
          const originalDate = new Date(year, month - 1, day, 12, 0, 0);

          // Find preferred day within the same week as the original fixture date.
          // Look back up to 3 days first (e.g. find Friday before a Sunday fixture),
          // then forward up to 3 days, then forward up to 10 days as last resort.
          // This keeps fixtures in the same weekend as the league-assigned date.
          const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
          let immediateNextPreferredDay = null;

          // Same week: scan -3 to +3 days relative to original date
          for (let offset = -3; offset <= 3; offset++) {
            const testDate = new Date(originalDate);
            testDate.setDate(originalDate.getDate() + offset);
            if (DAY_NAMES[testDate.getDay()] === preferredDay) {
              immediateNextPreferredDay = testDate.toISOString().split('T')[0];
              break;
            }
          }

          // Fallback: next occurrence of preferred day if not found in same week
          if (!immediateNextPreferredDay) {
            for (let offset = 4; offset <= 10; offset++) {
              const testDate = new Date(originalDate);
              testDate.setDate(originalDate.getDate() + offset);
              if (DAY_NAMES[testDate.getDay()] === preferredDay) {
                immediateNextPreferredDay = testDate.toISOString().split('T')[0];
                break;
              }
            }
          }

          const trySlot = (date, time, pitchId) => {
            if (!date) return null;
            const slot = checkSlotAvailability(date, time, pitchId, fixtureMatchArea, fixtureDuration);
            if (slot) {
              // Reserve these sections in the pending tracker
              slot.sections.forEach(section => {
                pendingSections.add(`${pitchId}|${date}|${time}|${section}`);
              });
            }
            return slot;
          };

          // Strategy 1: preferred day, preferred time window, preferred pitch
          if (immediateNextPreferredDay && !usedDates.has(immediateNextPreferredDay)) {
            const endHour = startHour + 3;
            outer1: for (let hour = startHour; hour < endHour && !foundSlot; hour++) {
              for (let minute = 0; minute < 60 && !foundSlot; minute += 15) {
                const testTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const slot = trySlot(immediateNextPreferredDay, testTime, preferredPitch);
                if (slot) {
                  newFixture.currentDate = slot.date;
                  newFixture.currentTime = slot.time;
                  newFixture.assignedPitch = slot.pitch;
                  newFixture.assignedSections = slot.sections;
                  newFixture.pitchName = availablePitches.find(p => p.id === slot.pitch)?.name || slot.pitch;
                  const isOriginal = slot.date === fixture.originalDate && slot.time === fixture.originalTime;
                  newFixture.optimizedReason = isOriginal ? `Original slot available` : `Strategy 1: ${preferredDay} at ${testTime} on ${newFixture.pitchName}`;
                  newFixture.optimized = !isOriginal;
                  usedDates.add(slot.date);
                  foundSlot = true; slotsFound++;
                  if (!isOriginal) slotsRescheduled++;
                }
              }
            }
          }

          // Strategy 2: preferred day, preferred time window, ANY pitch
          if (!foundSlot && immediateNextPreferredDay && !usedDates.has(immediateNextPreferredDay)) {
            const endHour = startHour + 3;
            for (let hour = startHour; hour < endHour && !foundSlot; hour++) {
              for (let minute = 0; minute < 60 && !foundSlot; minute += 15) {
                const testTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                for (const pitch of availablePitches) {
                  if (foundSlot) break;
                  const slot = trySlot(immediateNextPreferredDay, testTime, pitch.id);
                  if (slot) {
                    newFixture.currentDate = slot.date; newFixture.currentTime = slot.time;
                    newFixture.assignedPitch = slot.pitch; newFixture.assignedSections = slot.sections;
                    newFixture.pitchName = availablePitches.find(p => p.id === slot.pitch)?.name || slot.pitch;
                    newFixture.optimizedReason = `Strategy 2: ${preferredDay} at ${testTime} on ${newFixture.pitchName}`;
                    newFixture.optimized = true;
                    usedDates.add(slot.date);
                    foundSlot = true; slotsFound++; slotsRescheduled++;
                  }
                }
              }
            }
          }

          // Strategy 3: preferred day, any time, ANY pitch
          if (!foundSlot && immediateNextPreferredDay && !usedDates.has(immediateNextPreferredDay)) {
            for (let hour = 8; hour <= 21 && !foundSlot; hour++) {
              if (hour >= startHour && hour < startHour + 3) continue;
              for (let minute = 0; minute < 60 && !foundSlot; minute += 15) {
                const testTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                for (const pitch of availablePitches) {
                  if (foundSlot) break;
                  const slot = trySlot(immediateNextPreferredDay, testTime, pitch.id);
                  if (slot) {
                    newFixture.currentDate = slot.date; newFixture.currentTime = slot.time;
                    newFixture.assignedPitch = slot.pitch; newFixture.assignedSections = slot.sections;
                    newFixture.pitchName = availablePitches.find(p => p.id === slot.pitch)?.name || slot.pitch;
                    newFixture.optimizedReason = `Strategy 3: ${preferredDay} at ${testTime} on ${newFixture.pitchName} (outside preferred window)`;
                    newFixture.optimized = true;
                    usedDates.add(slot.date);
                    foundSlot = true; slotsFound++; slotsRescheduled++;
                  }
                }
              }
            }
          }

          // Strategy 4: flag for manual review
          if (!foundSlot) {
            newFixture.optimizedReason = 'No available slots found — manual review required';
            newFixture.optimized = false;
          }

        } catch (error) {
          console.error('Error optimizing fixture:', error);
          newFixture.optimizedReason = `Error: ${error.message}`;
        }

        optimizedFixtures.push(newFixture);
      }

      setFixtures(optimizedFixtures);
      setHasOptimized(true);
      showValidationModal('Optimization Complete', `Slots found: ${slotsFound}/${fixtures.filter(f => !f.isBye).length}\nRescheduled: ${slotsRescheduled}`, 'success');

    } catch (error) {
      console.error('AI optimization error:', error);
      showValidationModal('Optimization Failed', error.message, 'error');
    } finally {
      setAiProcessing(false);
    }
  };

  // ===================
  // SCHEDULE FIXTURE
  // ===================
  
  const scheduleFixtureToAllocator = async (fixture) => {
    if (!fixture.assignedPitch || !fixture.assignedSections || fixture.isBye) {
      showValidationModal('Cannot Schedule', 'Fixture must be optimized with pitch and sections assigned before scheduling.', 'warning');
      return;
    }

    if (!clubId || !selectedFacilityId) {
      showValidationModal('Missing Data', 'Missing required data. Please ensure all selections are complete.', 'error');
      return;
    }

    const teamObject = availableTeams.find(t => t.name === fixture.pitchalloTeam);
    if (!teamObject) {
      showValidationModal('Team Not Found', `Team "${fixture.pitchalloTeam}" not found in settings.`, 'error');
      return;
    }

    setSchedulingFixture(fixture.id);

    try {
      const startTime = fixture.currentTime || fixture.originalTime || '09:00';
      const fixtureDate = fixture.currentDate || fixture.originalDate;
      const assignedPitch = fixture.assignedPitch;
      const assignedSections = fixture.assignedSections;
      
      const durationStr = teamObject.matchDuration || teamObject.duration || '60';
      const baseDuration = parseInt(durationStr.toString().replace(/\D/g, '')) || 60;
      const durationWithBuffer = baseDuration + 10;
      const duration = Math.ceil(durationWithBuffer / 15) * 15;
      
      console.log('📅 [Scheduling] Duration calculation:', {
        rawDuration: teamObject.matchDuration || teamObject.duration,
        baseDuration,
        durationWithBuffer,
        finalDuration: duration
      });
      
      let normalizedPitch = assignedPitch.toString().toLowerCase().trim();
      
      const [hours, minutes] = startTime.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);
      
      const endDate = new Date(startDate.getTime() + (duration * 60 * 1000));
      const endTime = endDate.toTimeString().substring(0, 5);
      
      const slotsNeeded = Math.ceil(duration / 15);
      const createdTimestamp = Date.now();
      const bookingId = `fixture-${fixture.id || fixture.round}-${fixtureDate}-${startTime}-${createdTimestamp}`;
      
      const allocationsToSave = {};
      
      assignedSections.forEach((section) => {
        for (let slot = 0; slot < slotsNeeded; slot++) {
          const slotTime = new Date(startDate.getTime() + (slot * 15 * 60 * 1000));
          const slotTimeStr = slotTime.toTimeString().substring(0, 5);
          
          const allocationKey = `${fixtureDate}-${slotTimeStr}-${normalizedPitch}-${section}`;
          
          allocationsToSave[allocationKey] = {
            pitchId: normalizedPitch,
            pitchName: assignedPitch,
            sections: [section],
            date: fixtureDate,
            startTime: slotTimeStr,
            endTime: endTime,
            duration: duration,
            teamId: teamObject.id || teamObject.name,
            teamName: teamObject.name,
            allocationType: 'match',
            isRecurring: false,
            recurrencePattern: null,
            notes: `${fixture.homeTeam || ''} vs ${fixture.awayTeam || ''} - Round ${fixture.round || ''}`.trim(),
            bookingId: bookingId,
            clubId: clubId,
            facilityId: selectedFacilityId, // Include facility ID
            color: teamObject.color || '#6b7280',
            colour: teamObject.color || '#6b7280',
            created: createdTimestamp,
            createdBy: user?.email || 'fixture-scheduler@pitchallo.com',
            isMultiSlot: slotsNeeded > 1,
            slotIndex: slot,
            totalSlots: slotsNeeded,
            section: section,
            sectionGroup: assignedSections.join('+'),
            fixtureData: {
              round: fixture.round || '',
              homeTeam: fixture.homeTeam || '',
              awayTeam: fixture.awayTeam || '',
              competition: fixture.competition || ''
            }
          };
        }
      });

      // Check for conflicts
      const allocationsRef = collection(db, 'clubs', clubId, 'allocations', normalizedPitch, 'items');
      const existingSnapshot = await getDocs(allocationsRef);
      
      const existingKeys = new Set();
      existingSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.date === fixtureDate) {
          existingKeys.add(doc.id);
        }
      });

      const conflicts = Object.keys(allocationsToSave).filter(key => existingKeys.has(key));

      if (conflicts.length > 0) {
        showValidationModal('Scheduling Conflict', 'Cannot schedule - conflicts detected!\n\nPlease re-optimize to find available slots.', 'error');
        setSchedulingFixture(null);
        return;
      }

      // Save allocations
      const savePromises = [];
      for (const [key, allocation] of Object.entries(allocationsToSave)) {
        const allocationRef = doc(db, 'clubs', clubId, 'allocations', normalizedPitch, 'items', key);
        savePromises.push(setDoc(allocationRef, allocation));
      }
      
      await Promise.all(savePromises);
      
      // Update state
      setFixtures(prevFixtures => 
        prevFixtures.map(f => 
          f.id === fixture.id 
            ? { ...f, scheduled: true, scheduledAt: new Date().toISOString() }
            : f
        )
      );
      
      setScheduledFixtureIds(prev => new Set([...prev, fixture.id]));
      
      // Show success modal
      let pitchNumber = normalizedPitch.replace(/^pitch-?/, '');
      const pitchUrl = `/allocator/${pitchNumber}?date=${fixtureDate}&facility=${selectedFacilityId}&t=${Date.now()}`;
      
      setSchedulingSuccessModal({
        isOpen: true,
        fixture: {
          team: teamObject.name,
          date: fixtureDate,
          time: startTime,
          duration: duration,
          pitch: fixture.pitchName,
          sections: assignedSections.join(', '),
          matchArea: teamObject.matchArea
        },
        pitchUrl: pitchUrl
      });
      
    } catch (error) {
      console.error('Error scheduling fixture:', error);
      showValidationModal('Scheduling Failed', `Failed to schedule: ${error.message}`, 'error');
    } finally {
      setSchedulingFixture(null);
    }
  };

  // ===================
  // RESET FIXTURES
  // ===================
  
  const resetToOriginal = () => {
    setFixtures([...originalFixtures]);
    setHasOptimized(false);
    setScheduledFixtureIds(new Set());
  };

  // ===================
  // EXPORT FUNCTION
  // ===================
  
  const exportToPitchAllo = () => {
    if (fixtures.length === 0) {
      showValidationModal('No Fixtures', 'No fixtures to export. Please upload fixtures first.', 'warning');
      return;
    }

    const exportData = fixtures.map(f => ({
      date: f.currentDate || f.originalDate,
      time: f.currentTime || f.originalTime,
      pitch: f.pitchName || 'TBD',
      sections: f.assignedSections?.join(', ') || 'TBD',
      opponent: f.awayTeam,
      round: f.round,
      status: f.scheduled ? 'Scheduled' : f.optimized ? 'Optimized' : 'Pending',
      facilityId: selectedFacilityId,
      facilityName: selectedFacility?.name
    }));

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pitchallo-fixtures-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ===================
  // RENDER: LOADING STATE
  // ===================
  
  if (settingsLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 50%, #faf5ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '18px' }}>Loading Fixture Wizard...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ===================
  // RENDER: MOBILE BLOCK
  // ===================
  
  if (isMobileOrTablet) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 50%, #faf5ff 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <img src={logo} alt="Pitchallo" style={{ maxWidth: '100px', marginBottom: '24px' }} />
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '32px 24px',
          maxWidth: '400px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#fef3c7',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <AlertCircle size={32} style={{ color: '#f59e0b' }} />
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
            Desktop Required
          </h2>
          <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
            The Fixture Wizard needs a desktop for the best experience. Please switch to a larger screen.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // ===================
  // RENDER: WIZARD STEP CONTENT
  // ===================
  
  const renderStepContent = () => {
    const currentStepData = WIZARD_STEPS[currentStep];
    
    switch (currentStepData.id) {
      case 'facility':
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
              Select Your Facility
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
              Choose where you'll be hosting these fixtures
            </p>
            
            {facilities.length === 0 ? (
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center'
              }}>
                <AlertCircle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                  No Facilities Configured
                </h3>
                <p style={{ fontSize: '14px', color: '#a16207', marginBottom: '16px' }}>
                  Please set up at least one facility before scheduling fixtures.
                </p>
                <button
                  onClick={() => navigate('/facilities')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Facilities Setup
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {facilities.map((facility) => (
                  <button
                    key={facility.id}
                    onClick={() => {
                      setSelectedFacilityId(facility.id);
                      setSelectedFacility(facility);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '20px 24px',
                      backgroundColor: selectedFacilityId === facility.id ? '#ecfdf5' : 'white',
                      border: selectedFacilityId === facility.id ? '2px solid #10b981' : '2px solid #e5e7eb',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: '80px',
                      height: '60px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      backgroundColor: '#f3f4f6',
                      flexShrink: 0
                    }}>
                      {facility.satelliteImage ? (
                        <img 
                          src={facility.satelliteImage} 
                          alt={facility.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <Building2 size={24} style={{ color: '#9ca3af' }} />
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                          {facility.name}
                        </span>
                        {facility.id === clubData?.primaryFacilityId && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#10b981',
                            backgroundColor: '#d1fae5',
                            padding: '2px 8px',
                            borderRadius: '20px'
                          }}>
                            Primary
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6b7280' }}>
                        <MapPin size={14} />
                        <span>{facility.address || 'No address set'}</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                        {facility.pitches?.length || facility.pitchBoundaries?.length || facility.satelliteConfig?.pitchBoundaries?.length || facility.pitchCount || 0} pitches
                      </div>
                    </div>
                    {selectedFacilityId === facility.id && (
                      <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#10b981',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Check size={18} style={{ color: 'white' }} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
        
      case 'match-teams':
        return (
          <div style={{ maxWidth: '680px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', textAlign: 'center' }}>
              Match teams to your settings
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px', textAlign: 'center' }}>
              We found {detectedDriblTeams.length} team{detectedDriblTeams.length !== 1 ? 's' : ''} in the file you uploaded — We need to match these teams to your team names in Pitchallo.
            </p>

            {availableTeams.length === 0 ? (
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
                <AlertCircle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>No Teams Configured</h3>
                <p style={{ fontSize: '14px', color: '#a16207', marginBottom: '16px' }}>Please add teams in Club Settings before mapping fixtures.</p>
                <button onClick={() => navigate('/settings')} style={{ padding: '12px 24px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>
                  Go to Settings
                </button>
              </div>
            ) : (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CheckCircle size={18} style={{ color: '#10b981' }} />
                  <span style={{ fontSize: '14px', color: '#166534', fontWeight: '600' }}>
                    {selectedFacility?.name} · {detectedDriblTeams.reduce((a, t) => a + t.fixtureCount, 0)} home fixtures detected
                  </span>
                </div>

                <div style={{ padding: '24px', display: 'grid', gap: '12px' }}>
                  {detectedDriblTeams.map(({ driblName, fixtureCount, datelessCount = 0 }) => {
                    const mapped = teamMappings[driblName];
                    const isSkipped = mapped === '__skip__';
                    const isMatched = mapped && mapped !== '__skip__';
                    const prefs = getTeamPrefs(mapped || '');
                    return (
                      <div key={driblName} style={{
                        padding: '16px',
                        backgroundColor: isSkipped ? '#fafafa' : isMatched ? '#f0fdf4' : '#fffbeb',
                        borderRadius: '12px',
                        border: `1px solid ${isSkipped ? '#e5e7eb' : isMatched ? '#bbf7d0' : '#fde68a'}`
                      }}>
                        {/* Team name → mapping row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', alignItems: 'center', gap: '12px', marginBottom: isMatched ? '14px' : '0' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: isSkipped ? '#9ca3af' : '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isSkipped ? 'line-through' : 'none' }}>
                              {driblName}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                              {fixtureCount} home fixture{fixtureCount !== 1 ? 's' : ''}
                              {datelessCount > 0 && (
                                <span style={{ marginLeft: '6px', color: '#f59e0b', fontWeight: '500' }}>· {datelessCount} date TBD</span>
                              )}
                            </div>
                          </div>

                          <div style={{ color: '#9ca3af', fontSize: '18px', flexShrink: 0 }}>→</div>

                          <select
                            value={mapped || ''}
                            onChange={(e) => setTeamMappings(prev => ({ ...prev, [driblName]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '9px 12px',
                              border: `1px solid ${isSkipped ? '#e5e7eb' : isMatched ? '#10b981' : '#fbbf24'}`,
                              borderRadius: '8px',
                              fontSize: '13px',
                              backgroundColor: 'white',
                              color: '#1f2937',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="">— Select a team —</option>
                            {availableTeams.map(team => (
                              <option key={team.id || team.name} value={team.name}>{team.name}</option>
                            ))}
                            <option value="__skip__">Skip this team</option>
                          </select>

                          <div style={{ width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            {isMatched && <CheckCircle size={18} style={{ color: '#10b981' }} />}
                            {isSkipped && <X size={18} style={{ color: '#9ca3af' }} />}
                            {!mapped && <AlertCircle size={18} style={{ color: '#f59e0b' }} />}
                          </div>
                        </div>

                        {/* Per-team preferences — only shown when a team is matched */}
                        {isMatched && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '10px',
                            paddingTop: '14px',
                            borderTop: '1px solid #d1fae5'
                          }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>
                                Preferred day
                              </label>
                              <select
                                value={prefs.preferredDay}
                                onChange={(e) => updateTeamPref(mapped, 'preferredDay', e.target.value)}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', backgroundColor: 'white', cursor: 'pointer' }}
                              >
                                {['Sunday','Saturday','Friday','Thursday','Wednesday','Tuesday','Monday'].map(d => (
                                  <option key={d} value={d}>{d}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>
                                Preferred time
                              </label>
                              <select
                                value={prefs.preferredTimeSlot}
                                onChange={(e) => updateTeamPref(mapped, 'preferredTimeSlot', e.target.value)}
                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', backgroundColor: 'white', cursor: 'pointer' }}
                              >
                                {getAvailableTimeSlots().map(slot => (
                                  <option key={slot} value={slot}>{slot}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>
                                Preferred pitch
                              </label>
                              {availablePitches.length === 0 ? (
                                <div style={{ fontSize: '12px', color: '#92400e', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>No pitches</div>
                              ) : (
                                <select
                                  value={prefs.preferredPitch}
                                  onChange={(e) => updateTeamPref(mapped, 'preferredPitch', e.target.value)}
                                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                  {availablePitches.map(pitch => (
                                    <option key={pitch.id} value={pitch.id}>{pitch.name}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ padding: '16px 20px', backgroundColor: '#eff6ff', borderTop: '1px solid #bfdbfe', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <HelpCircle size={16} style={{ color: '#3b82f6', marginTop: '1px', flexShrink: 0 }} />
                  <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                    Any teams set to <strong>Skip</strong> won't be scheduled. You'll see a full list of what was skipped in the final summary so nothing is silently missed.
                  </p>
                </div>

                <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={applyTeamMappings}
                    disabled={!detectedDriblTeams.some(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__')}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: detectedDriblTeams.some(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__')
                        ? 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)'
                        : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: detectedDriblTeams.some(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__') ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Zap size={18} />
                    Continue — {detectedDriblTeams.filter(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__').reduce((a, t) => a + t.fixtureCount, 0)} fixtures to schedule
                  </button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'upload':
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', textAlign: 'center' }}>
              Upload Fixtures
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px', textAlign: 'center' }}>
              Import your fixture list and map the columns
            </p>
            
            {/* File Upload Area */}
            {!uploadedFileData ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '32px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                textAlign: 'center'
              }}>
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '16px',
                  padding: '48px 24px',
                  backgroundColor: '#fafafa',
                  marginBottom: '20px'
                }}>
                  <Upload size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Drop your fixture file here
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                    CSV or Excel (.xlsx) supported
                  </p>
                  <button
                    onClick={() => document.getElementById('file-upload').click()}
                    disabled={loading}
                    style={{
                      padding: '14px 32px',
                      background: loading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Reading file...' : 'Choose File'}
                  </button>
                </div>
                
                <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                  Export fixtures from Dribl or your league system
                </p>
                <button
                  onClick={() => setShowTemplateGuide(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    fontSize: '13px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  Not sure about the format? Download a template
                </button>
              </div>
            ) : (
              /* Column Mapping Interface */
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                {/* File Info Header */}
                <div style={{
                  padding: '16px 20px',
                  backgroundColor: '#f0fdf4',
                  borderBottom: '1px solid #bbf7d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CheckCircle size={20} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                      {uploadedFileData.length} rows loaded
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setUploadedFileData(null);
                      setDetectedHeaders([]);
                      setColumnMapping({
                        homeClubName: '',
                        awayClubName: '',
                        date: '',
                        time: '',
                        round: '',
                        homeTeam: ''
                      });
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Change File
                  </button>
                </div>
                
                {/* Column Mapping Form */}
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                      Map Your Columns
                    </h3>
                    <button
                      onClick={() => setShowTemplateGuide(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3b82f6',
                        fontSize: '13px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <HelpCircle size={13} />
                      File format guide
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                    Match each field to the correct column in your file
                  </p>

                  
                  {/* Club Name Filter */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                      Your Club Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={filterClubName}
                      onChange={(e) => setFilterClubName(e.target.value)}
                      placeholder="e.g., Eastern United"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Used to find your home games. For Dribl exports, match the Home Club Name column exactly. For FSA files, enter just your club name (e.g. "Eastern United") — it will be matched within team names.
                    </p>
                  </div>
                  
                  {/* Required Columns */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Home Club Column <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={columnMapping.homeClubName}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, homeClubName: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select column...</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Away Club Column <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={columnMapping.awayClubName}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, awayClubName: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select column...</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Date Column <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={columnMapping.date}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, date: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select column...</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                        Time Column
                      </label>
                      <select
                        value={columnMapping.time}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, time: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">None (default 09:00)</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Optional Columns */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f3f4f6'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '6px' }}>
                        Round Column <span style={{ fontSize: '12px', color: '#9ca3af' }}>(optional)</span>
                      </label>
                      <select
                        value={columnMapping.round}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, round: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">None</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '6px' }}>
                        Home Team Column <span style={{ fontSize: '12px', color: '#9ca3af' }}>(optional)</span>
                      </label>
                      <select
                        value={columnMapping.homeTeam}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, homeTeam: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Same as Home Club</option>
                        {detectedHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Preview Sample */}
                  {columnMapping.homeClubName && columnMapping.date && uploadedFileData.length > 0 && (
                    <div style={{
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '10px'
                    }}>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                        Sample Row Preview
                      </h4>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        <p><strong>Home:</strong> {uploadedFileData[0][columnMapping.homeClubName] || '—'}</p>
                        <p><strong>Away:</strong> {uploadedFileData[0][columnMapping.awayClubName] || '—'}</p>
                        <p><strong>Date:</strong> {uploadedFileData[0][columnMapping.date] || '—'}</p>
                        {columnMapping.time && <p><strong>Time:</strong> {uploadedFileData[0][columnMapping.time] || '—'}</p>}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Process Button */}
                <div style={{
                  padding: '16px 24px',
                  backgroundColor: '#f9fafb',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={processFixtures}
                    disabled={(!columnMapping.homeClubName && !columnMapping.homeTeam) || !columnMapping.date || !filterClubName.trim()}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: ((!columnMapping.homeClubName && !columnMapping.homeTeam) || !columnMapping.date || !filterClubName.trim()) 
                        ? '#9ca3af' 
                        : 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: ((!columnMapping.homeClubName && !columnMapping.homeTeam) || !columnMapping.date || !filterClubName.trim()) 
                        ? 'not-allowed' 
                        : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Zap size={18} />
                    Process Fixtures
                  </button>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'review':
        return (
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
                  Review & Schedule
                </h2>
                <p style={{ fontSize: '16px', color: '#6b7280' }}>
                  {fixtures.filter(f => !f.isBye).length} fixtures · {[...new Set(fixtures.map(f => f.pitchalloTeam).filter(Boolean))].length} team{[...new Set(fixtures.map(f => f.pitchalloTeam).filter(Boolean))].length !== 1 ? 's' : ''}
                  {fixtures.filter(f => f.dateStatus === 'tbd').length > 0 && (
                    <span style={{ marginLeft: '8px', color: '#f59e0b', fontSize: '14px' }}>
                      · {fixtures.filter(f => f.dateStatus === 'tbd').length} date TBD
                    </span>
                  )}
                </p>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={optimizeWithAI}
                  disabled={aiProcessing || hasOptimized}
                  style={{
                    padding: '12px 24px',
                    background: (aiProcessing || hasOptimized) ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (aiProcessing || hasOptimized) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {aiProcessing ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Optimizing...
                    </>
                  ) : (
                    <>
                      <Zap size={18} />
                      {hasOptimized ? 'Already Optimized' : 'Optimize with AI'}
                    </>
                  )}
                </button>
                
                {hasOptimized && (
                  <button
                    onClick={resetToOriginal}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <RefreshCw size={16} />
                    Reset
                  </button>
                )}
                
                <button
                  onClick={exportToPitchAllo}
                  style={{
                    padding: '12px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>
            
            {/* Fixture Table */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
            }}>
              <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Team</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Round</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Original Date</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Scheduled Date</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Pitch</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Sections</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Opponent</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                      <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixtures.map((fixture, index) => (
                      <tr 
                        key={index}
                        style={{
                          backgroundColor: scheduledFixtureIds.has(fixture.id) ? '#ecfdf5' : 
                                          fixture.scheduled ? '#e0e7ff' : 
                                          fixture.optimized ? '#f0fdf4' : 
                                          fixture.isBye ? '#fffbeb' : 'white',
                          borderBottom: '1px solid #f3f4f6'
                        }}
                      >
                        <td style={{ padding: '12px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#eff6ff', color: '#1e40af' }}>
                            {fixture.pitchalloTeam || fixture.homeTeam || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{fixture.round || '-'}</td>
                        <td style={{ padding: '12px', color: '#6b7280' }}>
                          {fixture.dateStatus === 'tbd'
                            ? <span style={{ fontSize: '12px', fontWeight: '600', padding: '2px 7px', borderRadius: '5px', backgroundColor: '#fef3c7', color: '#92400e' }}>TBD</span>
                            : (fixture.originalDate || '-')}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {fixture.dateStatus === 'tbd' && !scheduledFixtureIds.has(fixture.id) ? (
                            <input
                              type="date"
                              value={fixture.currentDate || ''}
                              onChange={(e) => updateFixtureDate(fixture.id, e.target.value)}
                              style={{
                                padding: '5px 8px',
                                border: '1px solid #fbbf24',
                                borderRadius: '6px',
                                fontSize: '12px',
                                backgroundColor: '#fffbeb',
                                color: '#92400e',
                                cursor: 'pointer',
                                width: '130px'
                              }}
                            />
                          ) : (
                            <span style={{ 
                              color: fixture.currentDate !== fixture.originalDate ? '#10b981' : '#1f2937',
                              fontWeight: fixture.currentDate !== fixture.originalDate ? '600' : '400'
                            }}>
                              {fixture.currentDate || '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ 
                            color: fixture.currentTime !== fixture.originalTime ? '#10b981' : '#1f2937',
                            fontWeight: fixture.currentTime !== fixture.originalTime ? '600' : '400'
                          }}>
                            {fixture.currentTime || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {fixture.pitchName ? (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {fixture.pitchName}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>TBD</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {fixture.assignedSections ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {fixture.assignedSections.map(section => (
                                <span key={section} style={{
                                  padding: '3px 8px',
                                  backgroundColor: '#d1fae5',
                                  color: '#047857',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  {section}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>TBD</span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {fixture.isBye ? (
                            <span style={{ fontStyle: 'italic', color: '#92400e' }}>BYE</span>
                          ) : (
                            <div>
                              <div style={{ fontWeight: '500' }}>vs {fixture.awayTeam}</div>
                              {fixture.optimizedReason && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {fixture.optimizedReason}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {scheduledFixtureIds.has(fixture.id) ? (
                            <span style={{
                              padding: '5px 10px',
                              backgroundColor: '#22c55e',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <CheckCircle size={12} />
                              DONE
                            </span>
                          ) : fixture.optimized ? (
                            <span style={{
                              padding: '5px 10px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              READY
                            </span>
                          ) : fixture.isBye ? (
                            <span style={{
                              padding: '5px 10px',
                              backgroundColor: '#fbbf24',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              BYE
                            </span>
                          ) : fixture.dateStatus === 'tbd' && !fixture.currentDate ? (
                            <span style={{
                              padding: '5px 10px',
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              DATE TBD
                            </span>
                          ) : (
                            <span style={{
                              padding: '5px 10px',
                              backgroundColor: '#6b7280',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              PENDING
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {!fixture.isBye && fixture.assignedPitch && fixture.assignedSections && !scheduledFixtureIds.has(fixture.id) && (
                            <button
                              onClick={() => scheduleFixtureToAllocator(fixture)}
                              disabled={schedulingFixture === fixture.id}
                              style={{
                                padding: '8px 14px',
                                backgroundColor: schedulingFixture === fixture.id ? '#9ca3af' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: schedulingFixture === fixture.id ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {schedulingFixture === fixture.id ? (
                                <>
                                  <div style={{
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid white',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }} />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Calendar size={12} />
                                  Schedule
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
        
      case 'summary':
        // Calculate summary statistics
        const scheduledFixtures = fixtures.filter(f => scheduledFixtureIds.has(f.id));
        const changedFixtures = scheduledFixtures.filter(f => 
          f.currentDate !== f.originalDate || f.currentTime !== f.originalTime
        );
        const unchangedFixtures = scheduledFixtures.filter(f => 
          f.currentDate === f.originalDate && f.currentTime === f.originalTime
        );
        const byeFixtures = fixtures.filter(f => f.isBye);
        const unscheduledFixtures = fixtures.filter(f => !scheduledFixtureIds.has(f.id) && !f.isBye);
        
        // Format date for display
        const formatDisplayDate = (dateStr) => {
          if (!dateStr) return 'TBD';
          const [year, month, day] = dateStr.split('-');
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString('en-AU', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short',
            year: 'numeric'
          });
        };
        
        // Generate email-friendly summary text
        const generateSummaryText = () => {
          let text = `FIXTURE CHANGES NOTIFICATION\n`;
          text += `${'='.repeat(50)}\n\n`;
          const mappedTeamNames = detectedDriblTeams.filter(t => teamMappings[t.driblName] && teamMappings[t.driblName] !== '__skip__').map(t => teamMappings[t.driblName]).join(', ');
          text += `Teams: ${mappedTeamNames || 'N/A'}\n`;
          text += `Facility: ${selectedFacility?.name || 'N/A'}\n`;
          text += `Date Generated: ${new Date().toLocaleDateString('en-AU', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}\n\n`;
          
          text += `SUMMARY\n`;
          text += `${'-'.repeat(30)}\n`;
          text += `• Total fixtures processed: ${fixtures.length - byeFixtures.length}\n`;
          text += `• Fixtures scheduled: ${scheduledFixtures.length}\n`;
          text += `• Fixtures with changes: ${changedFixtures.length}\n`;
          text += `• Fixtures unchanged: ${unchangedFixtures.length}\n`;
          if (unscheduledFixtures.length > 0) {
            text += `• Fixtures not scheduled: ${unscheduledFixtures.length}\n`;
          }
          if (byeFixtures.length > 0) {
            text += `• Bye rounds: ${byeFixtures.length}\n`;
          }
          text += `\n`;
          
          if (changedFixtures.length > 0) {
            text += `FIXTURES WITH CHANGES\n`;
            text += `${'='.repeat(50)}\n\n`;
            
            changedFixtures.forEach((fixture, index) => {
              text += `${index + 1}. Round ${fixture.round || 'N/A'} vs ${fixture.awayTeam}\n`;
              text += `   ORIGINAL: ${formatDisplayDate(fixture.originalDate)} at ${fixture.originalTime || 'TBD'}\n`;
              text += `   NEW:      ${formatDisplayDate(fixture.currentDate)} at ${fixture.currentTime || 'TBD'}\n`;
              text += `   Venue:    ${fixture.pitchName || 'TBD'}, Sections ${fixture.assignedSections?.join(', ') || 'TBD'}\n`;
              if (fixture.currentDate !== fixture.originalDate) {
                text += `   ⚠️ Date changed\n`;
              }
              if (fixture.currentTime !== fixture.originalTime) {
                text += `   ⚠️ Time changed\n`;
              }
              text += `\n`;
            });
          }
          
          if (unchangedFixtures.length > 0) {
            text += `FIXTURES UNCHANGED (scheduled as originally planned)\n`;
            text += `${'-'.repeat(50)}\n\n`;
            
            unchangedFixtures.forEach((fixture, index) => {
              text += `${index + 1}. Round ${fixture.round || 'N/A'} vs ${fixture.awayTeam}\n`;
              text += `   Date: ${formatDisplayDate(fixture.currentDate)} at ${fixture.currentTime || 'TBD'}\n`;
              text += `   Venue: ${fixture.pitchName || 'TBD'}, Sections ${fixture.assignedSections?.join(', ') || 'TBD'}\n`;
              text += `\n`;
            });
          }
          
          if (unscheduledFixtures.length > 0) {
            text += `FIXTURES NOT SCHEDULED (require manual attention)\n`;
            text += `${'-'.repeat(50)}\n\n`;
            
            unscheduledFixtures.forEach((fixture, index) => {
              text += `${index + 1}. Round ${fixture.round || 'N/A'} vs ${fixture.awayTeam}\n`;
              text += `   Original Date: ${formatDisplayDate(fixture.originalDate)} at ${fixture.originalTime || 'TBD'}\n`;
              // Include pitch name in reason if available
              let reason = fixture.optimizedReason || 'Not optimized';
              if (fixture.pitchName && reason.includes('preferred pitch')) {
                reason = reason.replace('preferred pitch', fixture.pitchName);
              } else if (fixture.pitchName && !reason.includes(fixture.pitchName)) {
                reason += ` on ${fixture.pitchName}`;
              }
              text += `   Reason: ${reason}\n`;
              text += `\n`;
            });
          }
          
          text += `\n${'='.repeat(50)}\n`;
          text += `Generated by Pitchallo Fixture Wizard\n`;
          
          return text;
        };
        
        const handleCopySummary = async () => {
          const text = generateSummaryText();
          try {
            await navigator.clipboard.writeText(text);
            setSummaryCopied(true);
            setTimeout(() => setSummaryCopied(false), 3000);
          } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setSummaryCopied(true);
            setTimeout(() => setSummaryCopied(false), 3000);
          }
        };
        
        return (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
              Changes Summary
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
              Review the fixture changes below. You can copy this summary and send it to your Football Federation to notify them of any date/time changes if required.
            </p>
            
            {/* Summary Stats Cards */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                backgroundColor: '#ecfdf5',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                border: '1px solid #a7f3d0'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669' }}>
                  {scheduledFixtures.length}
                </div>
                <div style={{ fontSize: '14px', color: '#047857', fontWeight: '500' }}>
                  Scheduled
                </div>
              </div>
              
              <div style={{
                backgroundColor: changedFixtures.length > 0 ? '#fef3c7' : '#f3f4f6',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                border: changedFixtures.length > 0 ? '1px solid #fcd34d' : '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: changedFixtures.length > 0 ? '#d97706' : '#6b7280' }}>
                  {changedFixtures.length}
                </div>
                <div style={{ fontSize: '14px', color: changedFixtures.length > 0 ? '#b45309' : '#6b7280', fontWeight: '500' }}>
                  Changed
                </div>
              </div>
              
              <div style={{
                backgroundColor: '#f3f4f6',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#6b7280' }}>
                  {unchangedFixtures.length}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                  Unchanged
                </div>
              </div>
              
              {unscheduledFixtures.length > 0 && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center',
                  border: '1px solid #fecaca'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626' }}>
                    {unscheduledFixtures.length}
                  </div>
                  <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: '500' }}>
                    Not Scheduled
                  </div>
                </div>
              )}
            </div>
            
            {/* Copy Button */}
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={handleCopySummary}
                style={{
                  padding: '14px 28px',
                  background: summaryCopied 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.2s ease'
                }}
              >
                {summaryCopied ? (
                  <>
                    <CheckCircle size={20} />
                    Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <Copy size={20} />
                    Copy Summary for Email
                  </>
                )}
              </button>
              <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                Click to copy the full summary text, then paste it into your email to Football Federation SA.
              </p>
            </div>
            
            {/* Changed Fixtures Section */}
            {changedFixtures.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px 20px',
                  backgroundColor: '#fef3c7',
                  borderBottom: '1px solid #fcd34d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <AlertCircle size={20} style={{ color: '#d97706' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                    Fixtures with Changes ({changedFixtures.length})
                  </h3>
                </div>
                <div style={{ padding: '16px' }}>
                  {changedFixtures.map((fixture, index) => (
                    <div 
                      key={fixture.id || index}
                      style={{
                        padding: '16px',
                        backgroundColor: '#fffbeb',
                        borderRadius: '10px',
                        marginBottom: index < changedFixtures.length - 1 ? '12px' : 0,
                        border: '1px solid #fde68a'
                      }}
                    >
                      <div style={{ 
                        fontWeight: '600', 
                        color: '#1f2937', 
                        marginBottom: '12px',
                        fontSize: '15px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>Round {fixture.round || 'N/A'} vs {fixture.awayTeam}</span>
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: '#fcd34d',
                          color: '#92400e',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          CHANGED
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fee2e2',
                          borderRadius: '8px',
                          border: '1px solid #fecaca'
                        }}>
                          <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase' }}>
                            Original
                          </div>
                          <div style={{ fontSize: '14px', color: '#7f1d1d', fontWeight: '500' }}>
                            {formatDisplayDate(fixture.originalDate)}
                          </div>
                          <div style={{ fontSize: '13px', color: '#991b1b' }}>
                            {fixture.originalTime || 'TBD'}
                          </div>
                        </div>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#dcfce7',
                          borderRadius: '8px',
                          border: '1px solid #bbf7d0'
                        }}>
                          <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase' }}>
                            New
                          </div>
                          <div style={{ fontSize: '14px', color: '#14532d', fontWeight: '500' }}>
                            {formatDisplayDate(fixture.currentDate)}
                          </div>
                          <div style={{ fontSize: '13px', color: '#166534' }}>
                            {fixture.currentTime || 'TBD'}
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '13px', color: '#6b7280' }}>
                        Venue: {fixture.pitchName || 'TBD'} • Sections: {fixture.assignedSections?.join(', ') || 'TBD'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Unchanged Fixtures Section */}
            {unchangedFixtures.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px 20px',
                  backgroundColor: '#ecfdf5',
                  borderBottom: '1px solid #a7f3d0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <CheckCircle size={20} style={{ color: '#059669' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#047857' }}>
                    Fixtures Unchanged ({unchangedFixtures.length})
                  </h3>
                </div>
                <div style={{ padding: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                    These fixtures were scheduled at their original date/time - no notification required.
                  </p>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {unchangedFixtures.map((fixture, index) => (
                      <div 
                        key={fixture.id || index}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '14px'
                        }}
                      >
                        <span style={{ color: '#374151', fontWeight: '500' }}>
                          Round {fixture.round || 'N/A'} vs {fixture.awayTeam}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          {formatDisplayDate(fixture.currentDate)} • {fixture.currentTime || 'TBD'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Unscheduled Fixtures Section */}
            {unscheduledFixtures.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px 20px',
                  backgroundColor: '#fef2f2',
                  borderBottom: '1px solid #fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <AlertCircle size={20} style={{ color: '#dc2626' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#b91c1c' }}>
                    Fixtures Not Scheduled ({unscheduledFixtures.length})
                  </h3>
                </div>
                <div style={{ padding: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                    These fixtures require manual attention - they could not be automatically scheduled.
                  </p>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {unscheduledFixtures.map((fixture, index) => (
                      <div 
                        key={fixture.id || index}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#fef2f2',
                          borderRadius: '8px',
                          border: '1px solid #fecaca'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#7f1d1d', fontWeight: '500', fontSize: '14px' }}>
                            Round {fixture.round || 'N/A'} vs {fixture.awayTeam}
                          </span>
                          <span style={{ color: '#991b1b', fontSize: '13px' }}>
                            Original: {formatDisplayDate(fixture.originalDate)}
                          </span>
                        </div>
                        {fixture.optimizedReason && (
                          <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '6px' }}>
                            Reason: {(() => {
                              let reason = fixture.optimizedReason;
                              if (fixture.pitchName && reason.includes('preferred pitch')) {
                                reason = reason.replace('preferred pitch', fixture.pitchName);
                              } else if (fixture.pitchName && !reason.includes(fixture.pitchName)) {
                                reason += ` on ${fixture.pitchName}`;
                              }
                              return reason;
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Skipped Teams */}
            {skippedTeams.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                marginBottom: '24px'
              }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <AlertCircle size={20} style={{ color: '#f59e0b' }} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                    Not scheduled — {skippedTeams.reduce((a, t) => a + t.fixtureCount, 0)} fixture{skippedTeams.reduce((a, t) => a + t.fixtureCount, 0) !== 1 ? 's' : ''} skipped
                  </h3>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                    These teams were not mapped. No fixtures were scheduled for them.
                  </p>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {skippedTeams.map(({ driblName, fixtureCount }) => (
                      <div key={driblName} style={{ padding: '12px 16px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#92400e', fontWeight: '500', fontSize: '14px' }}>{driblName}</span>
                        <span style={{ color: '#a16207', fontSize: '13px' }}>{fixtureCount} fixture{fixtureCount !== 1 ? 's' : ''} not scheduled</span>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '12px', lineHeight: '1.5' }}>
                    To schedule these teams, go back and add them in Team Settings, then re-run the wizard.
                  </p>
                </div>
              </div>
            )}

            {/* TBD Fixtures — league hasn't assigned dates yet */}
            {(() => {
              const tbdFixtures = fixtures.filter(f => f.dateStatus === 'tbd' && !f.scheduled);
              if (tbdFixtures.length === 0) return null;
              return (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  marginBottom: '24px'
                }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Clock size={20} style={{ color: '#f59e0b' }} />
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                      Awaiting league dates — {tbdFixtures.length} fixture{tbdFixtures.length !== 1 ? 's' : ''}
                    </h3>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                      These fixtures exist in the file but the league hasn't assigned dates yet. Go back to the Review step to enter dates manually once the league publishes them, then re-run the optimizer.
                    </p>
                    <div style={{ display: 'grid', gap: '6px' }}>
                      {tbdFixtures.map(f => (
                        <div key={f.id} style={{ padding: '10px 14px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: '500', fontSize: '14px', color: '#92400e' }}>{f.pitchalloTeam}</span>
                            <span style={{ fontSize: '13px', color: '#a16207', marginLeft: '8px' }}>vs {f.awayTeam}</span>
                            {f.round && <span style={{ fontSize: '12px', color: '#a16207', marginLeft: '8px' }}>· Round {f.round}</span>}
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '600', padding: '2px 8px', borderRadius: '5px', backgroundColor: '#fef3c7', color: '#92400e' }}>DATE TBD</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* No fixtures scheduled message */}
            {scheduledFixtures.length === 0 && (
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '16px',
                padding: '32px',
                textAlign: 'center'
              }}>
                <AlertCircle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                  No Fixtures Scheduled Yet
                </h3>
                <p style={{ fontSize: '14px', color: '#a16207' }}>
                  Go back to the Review step and schedule your fixtures before viewing the summary.
                </p>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  // ===================
  // MAIN RENDER
  // ===================
  
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dbeafe 50%, #faf5ff 100%)'
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src={logo} alt="Pitchallo" style={{ height: '40px' }} />
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              Fixture Wizard
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              AI-powered fixture scheduling for {clubName}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/menu')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Back to Menu
        </button>
      </div>

      {/* Wizard Progress */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {WIZARD_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = completedSteps.has(index);
              const isAccessible = index <= currentStep || isCompleted || canProceedToStep(index);
              
              // Skip facility step indicator if only 1 facility
              if (step.id === 'facility' && facilities.length <= 1) return null;
              
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isAccessible && goToStep(index)}
                    disabled={!isAccessible}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      cursor: isAccessible ? 'pointer' : 'not-allowed',
                      opacity: isAccessible ? 1 : 0.5,
                      padding: '8px 16px'
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: isCompleted ? '#10b981' : isActive ? '#3b82f6' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      {isCompleted ? (
                        <Check size={24} style={{ color: 'white' }} />
                      ) : (
                        <StepIcon size={24} style={{ color: isActive ? 'white' : '#6b7280' }} />
                      )}
                    </div>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: isActive ? '600' : '500',
                      color: isActive ? '#1f2937' : '#6b7280'
                    }}>
                      {step.title}
                    </span>
                  </button>
                  
                  {index < WIZARD_STEPS.length - 1 && !(step.id === 'facility' && facilities.length <= 1) && (
                    <div style={{
                      flex: 1,
                      height: '2px',
                      backgroundColor: isCompleted ? '#10b981' : '#e5e7eb',
                      margin: '0 8px',
                      marginBottom: '28px'
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div style={{ padding: '40px 32px', animation: 'fadeIn 0.3s ease' }}>
        {renderStepContent()}
      </div>

      {/* Navigation Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={goToPreviousStep}
          disabled={currentStep === 0}
          style={{
            padding: '12px 24px',
            backgroundColor: 'transparent',
            color: currentStep === 0 ? '#9ca3af' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '500',
            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: currentStep === 0 ? 0.5 : 1
          }}
        >
          <ChevronLeft size={18} />
          Previous
        </button>
        
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Step {facilities.length <= 1 ? currentStep : currentStep + 1} of {facilities.length <= 1 ? WIZARD_STEPS.length - 1 : WIZARD_STEPS.length}
        </div>
        
        {currentStep < WIZARD_STEPS.length - 1 && (
          <button
            onClick={goToNextStep}
            disabled={!canProceedToStep(currentStep + 1)}
            style={{
              padding: '12px 24px',
              background: canProceedToStep(currentStep + 1) ? 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: canProceedToStep(currentStep + 1) ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Continue
            <ChevronRight size={18} />
          </button>
        )}
        
        {currentStep === WIZARD_STEPS.length - 1 && (
          <button
            onClick={() => navigate('/menu')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <CheckCircle size={18} />
            Finish
          </button>
        )}
      </div>

      {/* Success Modal */}
      {schedulingSuccessModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '480px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#d1fae5',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle size={32} style={{ color: '#10b981' }} />
            </div>
            <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
              Fixture Scheduled!
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
              {schedulingSuccessModal.fixture?.team} on {schedulingSuccessModal.fixture?.date} at {schedulingSuccessModal.fixture?.time}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setSchedulingSuccessModal({ isOpen: false, fixture: null, pitchUrl: null })}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Continue Scheduling
              </button>
              <button
                onClick={() => {
                  navigate(schedulingSuccessModal.pitchUrl);
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                View in Allocator
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Guide Modal */}
      {showTemplateGuide && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                Fixture File Guide
              </h3>
              <button
                onClick={() => setShowTemplateGuide(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} style={{ color: '#6b7280' }} />
              </button>
            </div>
            
            <div style={{ padding: '24px 28px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                  Supported formats
                </h4>
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '16px', fontSize: '14px', color: '#374151' }}>
                  <p style={{ fontWeight: '600', marginBottom: '8px' }}>Dribl export (recommended)</p>
                  <p style={{ marginBottom: '4px', color: '#6b7280' }}><strong>Home Club Name</strong> — column used to filter your home games</p>
                  <p style={{ marginBottom: '4px', color: '#6b7280' }}><strong>Home Team</strong> — team name (e.g. "Eastern United Under 12's YPL B")</p>
                  <p style={{ marginBottom: '4px', color: '#6b7280' }}><strong>Away Club Name</strong> — opponent club</p>
                  <p style={{ marginBottom: '4px', color: '#6b7280' }}><strong>Date</strong> — YYYY-MM-DD</p>
                  <p style={{ marginBottom: '4px', color: '#6b7280' }}><strong>Start</strong> — kick-off time (HH:MM)</p>
                  <p style={{ marginBottom: '12px', color: '#6b7280' }}><strong>Round</strong> — round number</p>
                  <p style={{ fontWeight: '600', marginBottom: '8px' }}>Manual / custom format</p>
                  <p style={{ color: '#6b7280' }}>Any CSV or Excel file works — you'll map your columns to the right fields in the next step. Download the template below to see the expected structure.</p>
                </div>
              </div>

              <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                  <strong>Tip:</strong> Export directly from Dribl and the columns will be detected automatically. The wizard filters to your club's home games only — away fixtures are ignored.
                </p>
              </div>
              
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/FixtureWizard_Template.xlsx';
                  link.download = 'FixtureWizard_Template.xlsx';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  setShowTemplateGuide(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Download size={18} />
                Download template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {validationModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: validationModal.type === 'success' ? '#d1fae5' : 
                              validationModal.type === 'warning' ? '#fef3c7' : '#fee2e2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              {validationModal.type === 'success' ? (
                <CheckCircle size={32} style={{ color: '#10b981' }} />
              ) : validationModal.type === 'warning' ? (
                <AlertCircle size={32} style={{ color: '#f59e0b' }} />
              ) : (
                <X size={32} style={{ color: '#ef4444' }} />
              )}
            </div>
            <h3 style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              color: '#1f2937', 
              marginBottom: '12px' 
            }}>
              {validationModal.title}
            </h3>
            <p style={{ 
              fontSize: '15px', 
              color: '#6b7280', 
              marginBottom: '24px',
              whiteSpace: 'pre-line',
              lineHeight: '1.6'
            }}>
              {validationModal.message}
            </p>
            <button
              onClick={closeValidationModal}
              style={{
                width: '100%',
                padding: '14px 24px',
                backgroundColor: validationModal.type === 'success' ? '#10b981' : 
                                validationModal.type === 'warning' ? '#f59e0b' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {validationModal.type === 'success' ? 'Continue' : 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      <PremiumFeatureModal
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          navigate('/');
        }}
        featureName="Fixture Wizard"
        currentTier={subscriptionTier}
        requiredTiers={['premium']}
        benefits={[
          'AI-powered fixture optimization',
          'Smart pitch allocation by team requirements',
          'Conflict detection across all allocations',
          'Direct scheduling to your pitch allocator',
          'Multi-facility support'
        ]}
        upgradeUrl="/pricing"
      />
      
      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => {
          setShowTrialExpiredModal(false);
          navigate('/');
        }}
      />
    </div>
  );
};

export default FixtureScheduler;
